/**
 * Sign-in for the console.
 *
 * ## Why this exists
 *
 * Until this module, the console had no sign-in at all. `getClerkUserId`
 * resolved a caller only via `DEV_AUTH_BYPASS_USER_ID`, so every browser
 * session was one hardcoded identity and nothing org-shaped — the workspace
 * switcher, org-owned job sites, Policies' admin gate, Invite, or one teammate
 * reviewing another's record — could be exercised by a human at all.
 *
 * ## The three states, kept distinct on purpose
 *
 * 1. **No publishable key** → the console runs UNGATED. This is the dev-auth
 *    bypass path, and it is why the whole test suite still passes: a console
 *    with no Clerk configured must behave exactly as it did before sign-in
 *    existed, rather than locking everyone out of a working local setup.
 * 2. **Key present, signed out** → the sign-in screen (`SignInScreen.tsx`:
 *    the brand panel is ours, the form is Clerk's, themed). Not an empty
 *    console, and not a blank page: "you are signed out" and "there is no
 *    data" must never look alike.
 * 3. **Key present, signed in** → the console, with the token published for
 *    `api.ts` and the live connection to read, an identity control (Clerk's
 *    `<UserButton>`) handed to the shell's sidebar footer, and the whole tree
 *    scoped to the signed-in user id so switching accounts rebuilds it.
 *
 * State 1 is a real deployment mode, not a test hack — but it is also the one
 * that would silently serve everyone as one user if a key went missing in
 * production. `backend`'s own bypass is double-gated on
 * `NODE_ENV === "development"`, so a prod console with no key authenticates
 * NOBODY and every request 401s. Loud, not silent.
 */

import { useEffect, useState, type ReactNode } from "react";
import { consoleUrl, setMountPath } from "../basePath";
import {
  ClerkProvider,
  SignedIn,
  SignedOut,
  UserButton,
  useAuth,
  useUser,
} from "@clerk/clerk-react";
import { AUTH_LOCALIZATION } from "./authFrame";
import { SignInScreen } from "./SignInScreen";
import { SignUpScreen } from "./SignUpScreen";
import { setAuthTokenAccessor } from "./token";
import { USER_MENU_APPEARANCE } from "./userMenu";
import { openIntegrations } from "../components/integrationsEvents";
import {
  AuthIdentity,
  AuthIdentityContext,
  AuthPersonContext,
  IdentityScope,
  personFromClerkUser,
} from "./identity";

// Re-exported because this is still where "the console's identity control"
// belongs conceptually; the component itself lives in `identity.tsx` so the
// SHELL can mount it without importing Clerk. See that file's header.
export { AuthIdentity };

/** Runtime config, injected by `/__env.js` (a classic script `index.html`
 *  loads BEFORE the module bundle — see that route's comment for why it is not
 *  fetched). Same idea as `backend/`'s own `/__env` for its WebView: config the
 *  bundler cannot reliably inline. */
interface ConsoleEnv {
  CLERK_PUBLISHABLE_KEY?: string;
  MOUNT_PATH?: string;
}

/**
 * Fetch the runtime config. Called by `main.tsx` at module top level, BEFORE
 * `createRoot` — see `/__env`'s route comment for why this cannot happen
 * during render.
 *
 * A failure resolves to null, meaning "no Clerk configured", which takes the
 * ungated path. Safe for the same reason the empty-key case is: the SERVER
 * decides every request, and `backend`'s bypass is double-gated on
 * `NODE_ENV === "development"`, so a production console with no key
 * authenticates NOBODY and every request 401s. Refusing to render would be
 * worse — it would hide a working local setup behind a failure unrelated to
 * auth.
 */
export async function loadPublishableKey(): Promise<string | null> {
  try {
    const response = await fetch(consoleUrl("/__env"), { headers: { accept: "application/json" } });
    const body = (await response.json()) as ConsoleEnv;
    setMountPath(body?.MOUNT_PATH ?? "");
    return body?.CLERK_PUBLISHABLE_KEY || null;
  } catch {
    return null;
  }
}

/**
 * Publishes WHO is signed in, from Clerk, to the shell's identity strip.
 *
 * ## Why the name cannot come from the server
 *
 * The console's `caller` envelope is `resolveCallerIdentity`, which reads the
 * Mongo `users` mirror. Those rows carry only `userId`, `clerkUserId` and
 * integration tokens — no name and no email. `upsertUserFromClerk` writes
 * them and runs only from the Clerk webhook, which never populated these
 * accounts, so the server correctly found nothing and the sidebar footer
 * printed `user_3E3fUozuA7b…`. Verified directly against the staging database.
 *
 * Clerk, in this very browser, has had the person's full profile the whole
 * time. This publishes it. The server's `caller` stays as the fallback for
 * the UNGATED console, which has no Clerk to ask — see `resolveIdentity`.
 *
 * Rendered inside `<SignedIn>`, so there is always a session; `useUser` can
 * still answer `null` for a beat while Clerk loads, which `personFromClerkUser`
 * maps to `null` — "the auth layer knows nobody yet", which falls through to
 * the mirror rather than blanking the strip.
 */
function ClerkPerson({ children }: { children: ReactNode }) {
  const { user } = useUser();
  return (
    <AuthPersonContext.Provider value={personFromClerkUser(user)}>
      {children}
    </AuthPersonContext.Provider>
  );
}

/**
 * Publishes Clerk's `getToken` to the plain-function consumers.
 *
 * Rendered inside `<SignedIn>`, so it mounts only once there IS a session, and
 * clears the accessor on unmount — which is what makes sign-out actually stop
 * sending a credential rather than leaving a stale closure behind.
 */
function ClerkTokenBridge({ children }: { children: ReactNode }) {
  const { getToken, isLoaded, userId } = useAuth();

  // Published DURING RENDER, not in an effect.
  //
  // React runs effects child-first: this component is Shell's PARENT, so an
  // effect here fires AFTER Shell's own mount effect — the one that issues the
  // first data request. Publishing from an effect therefore let that first
  // request go out with no credential at all. It 401s, the view renders "You
  // are not signed in", and nothing retries: a fully signed-in console stuck
  // on an auth error. That is what shipped, and only a browser showed it.
  //
  // Assignment to a module-level holder is idempotent and has no cleanup
  // ordering to get wrong, so doing it in render is safe here and is the only
  // way to guarantee the accessor exists before any child can ask for it.
  setAuthTokenAccessor(() => getToken());

  useEffect(() => () => setAuthTokenAccessor(null), []);

  // Clerk resolves the session asynchronously. Before `isLoaded`, `getToken()`
  // answers null even for a signed-in user — so rendering children here would
  // reintroduce the same unauthenticated first request by a different route.
  //
  // `isLoaded` alone was NOT enough, measured in a browser: the console's very
  // first `/api/items` still went out with no `authorization` header while
  // later ones carried it. Children therefore wait for a token to have been
  // obtained ONCE, not merely for Clerk to report itself loaded. The shell
  // mounts knowing a credential exists.
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!isLoaded) return;
    let cancelled = false;
    void (async () => {
      try {
        await getToken();
      } catch {
        // A token we could not mint is still an answer: render, let the
        // request 401, and show "signed out" rather than hanging on a blank
        // page forever.
      }
      if (!cancelled) setReady(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [isLoaded, getToken]);

  if (!isLoaded || !ready) return null;

  // Everything below belongs to THIS user. A different one gets a different
  // console — see `IdentityScope`. Clerk's multi-session "switch account"
  // does not unmount `<SignedIn>`, so without this the console would keep the
  // previous account's rows, its `useResource` state and a live stream opened
  // under the old credential while the identity strip named someone else.
  return <IdentityScope identity={userId ?? null}>{children}</IdentityScope>;
}

/**
 * The signed-in identity, always visible — Clerk's own control.
 *
 * Deliberately prominent rather than tucked away: the user will switch between
 * two accounts repeatedly while testing team behaviour, and the single most
 * confusing way to lose an hour is to run a test as the wrong identity without
 * noticing. `UserButton` carries sign-out AND "add account", so both leaving
 * and switching are one click — and switching is the only route to an
 * organization the current account does not belong to.
 *
 * Handed to the shell through `AuthIdentityContext` rather than imported by
 * it: the shell must keep working with no Clerk at all.
 */
/** Signing out returns you to the console — the MOUNTED console.
 *  Hard-coded `"/"` sent a user signing out of `ledai.build/app` to the
 *  marketing site at that domain's root, which reads as being thrown out of
 *  the product rather than out of the session. */
/** The design's bolt — the same glyph Ask Led AI uses, at the menu's 16px. */
function IntegrationsGlyph() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden style={{ display: "block" }}>
      <path d="M13 2L4 14h7l-1 8 9-12h-7l1-8z" fill="currentColor" />
    </svg>
  );
}

const CLERK_USER_BUTTON = (
  <SignedIn>
    {/* The popover this opens is design 3a, themed in `userMenu.ts` — see
        that file for why the menu stays Clerk's rather than becoming ours. */}
    <UserButton appearance={USER_MENU_APPEARANCE} afterSignOutUrl={consoleUrl("/")}>
      {/* Integrations is the user's OWN Procore/ACC sign-ins — account
          settings, so it lives here with Manage account, as the design draws
          it. Added through Clerk's own menu API rather than a rebuilt menu, so
          adding and switching accounts keep working (see `userMenu.ts`). The
          reserved labels pin Clerk's two items around it. */}
      <UserButton.MenuItems>
        <UserButton.Action label="manageAccount" />
        <UserButton.Action label="Integrations" labelIcon={<IntegrationsGlyph />} onClick={openIntegrations} />
        <UserButton.Action label="signOut" />
      </UserButton.MenuItems>
    </UserButton>
  </SignedIn>
);

/**
 * Is this the sign-up door?
 *
 * A PATH rather than a query parameter or a hash, because Clerk navigates
 * between the two screens by URL and both have to survive a reload: the
 * server serves the SPA at `/sign-up` and `basePath.ts` knows that segment is
 * a page rather than part of the mount. Both of those must stay in step with
 * this — see the warning in `basePath.ts`.
 *
 * Read at render rather than stored: the only way to cross between the two
 * screens is a real navigation, so there is no state here to keep.
 */
function isSignUpPath(): boolean {
  if (typeof location === "undefined") return false;
  return /\/sign-up\/?$/.test(location.pathname);
}

/**
 * Wraps the console. See the file header for the three states.
 */
export function AuthGate({
  publishableKey,
  children,
}: {
  /** Resolved by `main.tsx` before the first render. `null` = no Clerk
   *  configured, which is the ungated path. */
  publishableKey: string | null;
  children: ReactNode;
}) {
  if (!publishableKey) {
    // State 1 — no Clerk configured. Ungated by design; see the file header.
    return <>{children}</>;
  }

  return (
    // Every Clerk redirect target is MOUNTED. Clerk defaults them to the host
    // root, and on a path-mounted console that lands the user on whatever
    // else lives at that domain — for `ledai.build/app`, the marketing site.
    // Verified in a browser: a ticket sign-in at `/app` redirected to `/`,
    // and because the base path is derived from `location.pathname` at module
    // load, the reloaded page then made every request unmounted.
    <ClerkProvider
      publishableKey={publishableKey}
      afterSignOutUrl={consoleUrl("/")}
      signInFallbackRedirectUrl={consoleUrl("/")}
      signUpFallbackRedirectUrl={consoleUrl("/")}
      // Both screens' wording, where the design's differs from Clerk's
      // defaults. On the PROVIDER rather than on the components so every
      // Clerk surface inherits the same strings — see `AUTH_LOCALIZATION`.
      localization={AUTH_LOCALIZATION}
      // Clerk's own cross-links. Without these, "Sign up" on the sign-in
      // screen leaves the console for Clerk's hosted account portal — a page
      // with Clerk's branding, none of ours, and no way back that a visitor
      // would recognise. Verified in a browser: the link pointed at
      // `<instance>.accounts.dev/sign-up`.
      signInUrl={consoleUrl("/")}
      signUpUrl={consoleUrl("/sign-up")}
    >
      <SignedOut>
        {isSignUpPath() ? <SignUpScreen /> : <SignInScreen />}
      </SignedOut>
      <SignedIn>
        <AuthIdentityContext.Provider value={CLERK_USER_BUTTON}>
          <ClerkPerson>
            <ClerkTokenBridge>{children}</ClerkTokenBridge>
          </ClerkPerson>
        </AuthIdentityContext.Provider>
      </SignedIn>
    </ClerkProvider>
  );
}
